<template>
  <div class="jr-table">
    <el-table
      border
      row-class-name="table-row"
      header-row-class-name="table-header-cell"
      :data="tableData"
      v-loading="loading"
      style="width: 100%"
      ref="jrtable"
      v-bind="$attrs"
      v-on="$listeners"
    >
      <template slot="empty">
        <span v-if="!loading && !$slots.emptyExtra">暂无数据</span>
        <slot v-else name="emptyExtra" />
      </template>
      <template v-for="column in tableColumn">
        <el-table-column v-if="column.type" :key="column.key || column.prop" v-bind="tableColumnItem(column)" />
        <el-table-column
          v-else-if="column.show ? column.show() : true"
          :key="column.key || column.prop"
          :align="column.align || 'left'"
          v-bind="tableColumnItem(column)"
        >
          <!-- 自定义表头 -->
          <template slot="header">
            <template v-if="column.headerSlotName">
              <slot :name="column.headerSlotName" :column="column" />
            </template>
            <span v-else>{{column.label}}</span>
          </template>
          <template v-if="column.isMultiColumn">
            <slot :name="column.multiColumnSlotName" :column="column" />
          </template>
          <template slot-scope="{row, $index}">
            <template v-if="column.slotName">
              <!-- 通过slot自定义列：参数，当前行 -->
              <slot :name="column.slotName" :row="row" :index="$index" :column="column" />
            </template>
            <template v-else-if="column.render">
              <span v-html="column.render(row[column.prop], row)"></span>
            </template>
            <template v-else-if="column.btnGroups && column.btnGroups.length > 0">
              <div class="btn-group">
                <template v-for="btnItem in column.btnGroups">
                  <span v-if="btnItem.btnSlotName && (btnItem.showBtn === undefined || (btnItem.showBtn !== undefined && btnItem.showBtn(row)))" :key="btnItem.name">
                    <slot :name="btnItem.btnSlotName" :row="row" :index="$index" />
                  </span>
                  <el-popconfirm
                    v-else-if="(btnItem.showBtn === undefined || (btnItem.showBtn !== undefined && btnItem.showBtn(row))) && btnItem.confirmText"
                    :key="btnItem.name"
                    :title="confirmText(btnItem, row)"
                    @confirm="btnItem.onClick(row)"
                  >
                    <el-button
                      slot="reference"
                      :size="btnItem.size || 'mini'"
                      :type="btnItem.type"
                      v-track
                      :trackCode="btnItem.trackCode || ''"
                    >{{btnItem.name}}</el-button>
                  </el-popconfirm>
                  <template v-else>
                    <el-button
                      v-if="btnItem.showBtn === undefined || (btnItem.showBtn !== undefined && btnItem.showBtn(row))"
                      :key="btnItem.name"
                      :size="btnItem.size || 'mini'"
                      :class="btnItem.className"
                      :loading="row.loading"
                      v-bind="btnProps(btnItem)"
                      @click="btnItem.click(row)"
                      v-track
                      :trackCode="btnItem.trackCode || ''"
                    >
                      {{btnItem.name}}
                    </el-button>
                  </template>
                </template>
              </div>
            </template>
            <template v-else-if="column.permission">
              <div
                v-if="$has(column.permission)"
                class="table-link-cell"
                v-track
                :trackCode="column.trackCode || ''"
                @click="column.click ? column.click(row) : null"
              >
                {{row[column.prop] || '-'}}
              </div>
              <div v-else>{{row[column.prop] || '-'}}</div>
            </template>
            <template v-else-if="column.click && !column.filterName">
              <div
                :class="[column.isLink ? (column.isLink(row) ? 'table-link-cell' : '') : 'table-link-cell']"
                v-track
                :trackCode="column.trackCode || ''"
                @click="column.click ? column.click(row) : null"
              >
                {{row[column.prop] || '-'}}
              </div>
            </template>
            <status
              v-else-if="column.status"
              type="dot"
              :color="column.statusColor(row[column.status])"
              :text="row[column.statusDesc] || '-'"
            />
            <span
              v-else
              :class="column.click ? 'table-link-cell' : ''"
              @click="column.click ? column.click(row) : null"
            >
              <template v-if="column.filterName">
                <template v-if="typeof(column.filterName) === 'string'">
                  <span>{{transformFilter(row[column.prop], column.filterName)}}</span>
                </template>
                <div
                  v-else
                  v-html="column.filterName(row[column.prop], column)"
                >
                </div>
              </template>
              <template v-else>
                <span>
                  {{valFilter(row[column.prop])}}
                </span>
              </template>
            </span>
          </template>
        </el-table-column>
      </template>
    </el-table>
    <div class="footer" v-if="pagination">
      <pagination
        :total="total"
        :current-page="searchParams.page"
        :size="searchParams.limit"
        @current-change="handleCurrentChange"
        @size-change="handleSizeChange"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: '',
      // required: true,
    },
    // 表格数据
    tableData: {
      type: Array,
      default: () => ([]),
    },
    // 若传入了pagination，则需要传入列表总条数
    total: {
      type: Number,
      default: 0,
    },
    limit: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 0,
    },
    // 关联数据源列表 表头 或者 vNode
    tableColumn: {
      type: Array,
      default: () => ([]),
    },
    // 搜索参数 因为子组件需要修改该对象，so父级需要加.sync后缀，
    searchParams: {
      type: Object,
      default() { return {}; },
    },
    // 是否需要分页 [可选]
    pagination: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    tableColumnItem() {
      return (items) => {
        const { btnGroups, ...props } = items;
        return {
          minWidth: '1',
          ...props,
        };
      };
    },
    btnProps() {
      return (items) => {
        const { name, ...props } = items;
        return props;
      };
    },
  },
  data() {
    return {};
  },
  methods: {
    // 对 Table 进行重新布局
    doLayout() {
      this.$refs.jrtable.doLayout();
    },
    handleCurrentChange(page) {
      this.searchParams.page = page;
      this.$emit('refresh', { page });
    },
    handleSizeChange(limit) {
      this.searchParams.limit = limit;
      this.$emit('refresh', { page: this.searchParams.page, limit });
    },
    valFilter(val) {
      if (!val && typeof val !== 'number') {
        return '-';
      }
      return val;
    },
    /**
     * @desc 过滤器使用
     * @param {value} val 原值
     * @param {filterNames} filterNames 过滤器名称
     * @return {newValue} String
     */
    transformFilter(val, filterNames) {
      if (!val && typeof val !== 'number') {
        return '-';
      }
      const filters = _.map(_.split(filterNames, '|'), _.trim);
      const newVal = _.reduce(filters, (vals, funStr) => {
        const funName = funStr.substring(0, funStr.indexOf('(')) || funStr;
        const funParam = _.trim(funStr.substring(funStr.indexOf('(') + 1, funStr.indexOf(')')));
        let paramList = [];
        if (funParam) {
          paramList = funParam.split(',');
        }
        if (!this.$options.filters[funName]) return vals;
        return this.$options.filters[funName].apply(this, [vals, ...paramList]);
      }, val);
      return newVal;
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .el-table__empty-text {
    padding-top: 20px;
  }
}

.footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 20px;
}
</style>
